import type { Dayjs, OptionType } from 'dayjs';
import dayjs, { type ConfigType, type ManipulateType, type OpUnitType, type UnitType } from 'dayjs';
import localeData from 'dayjs/plugin/localeData';
import objectSupport from 'dayjs/plugin/objectSupport';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import utc from 'dayjs/plugin/utc';
import isBetween from 'dayjs/plugin/isBetween';
import timezone from 'dayjs/plugin/timezone';
import quarterOfYear from 'dayjs/plugin/quarterOfYear';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';

export const eUSTimeZone = 'Europe/Warsaw';

import type { DateFormat, TimeFormat } from '../../models/dateFormats';
import type { AvailableLanguages } from '../../models/translation.model';
import type { DateExclusivity } from './interfaces';

import 'dayjs/locale/pl';
import 'dayjs/locale/uk';
import 'dayjs/locale/en-gb';

// FOR FUTURE
// https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Temporal#description;

//PLUGINS
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(objectSupport);
dayjs.tz.setDefault();
dayjs.extend(isBetween);
dayjs.extend(localeData);
dayjs.extend(customParseFormat);
dayjs.extend(quarterOfYear);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

export interface AppDateObjectSupport {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
}

const hasZoneInfo = (isoString: string) => /(\d{4}-[01]\d-[0-3]\dT[0-2]\d:[0-5]\d:[0-5]\d\.\d+([+-][0-2]\d:[0-5]\d|Z))|(\d{4}-[01]\d-[0-3]\dT[0-2]\d:[0-5]\d:[0-5]\d([+-][0-2]\d:[0-5]\d|Z))|(\d{4}-[01]\d-[0-3]\dT[0-2]\d:[0-5]\d([+-][0-2]\d:[0-5]\d|Z))/.test(isoString);

export default class AppDate {
  date: Dayjs;

  constructor(dateInput?: ConfigType | AppDateObjectSupport, format?: OptionType, strict?: boolean) {
    if (dayjs.isDayjs(dateInput)) {
      this.date = dateInput;

      return;
    } if (!dateInput) {
      this.date = AppDate.now();

      return;
    } if(typeof dateInput === 'string') {
      this.date = hasZoneInfo(dateInput) ? AppDate.fromISO(dateInput) : AppDate.fromString(dateInput, format, strict);

      return;
    } if (dateInput instanceof Date) {
      this.date = AppDate.fromDate(dateInput);

      return;
    } if (typeof dateInput === 'object') {
      this.date = AppDate.fromParts(dateInput as AppDateObjectSupport);

      return;
    }

    throw new Error('Unsupported input for AppDate');
  }

  private static fromString(input: string, format?: OptionType, strict: boolean = false): Dayjs {
    const tz = eUSTimeZone;

    if (format) {
      const parsed = dayjs(input, format, strict);

      return parsed.isValid()
        ? dayjs.tz(parsed.format('YYYY-MM-DDTHH:mm:ss'), tz)
        : parsed;
    }

    // Fallback: nieznany format → najpierw parsujemy bez tz
    const parsed = dayjs(input);

    return parsed.isValid()
      ? dayjs.tz(parsed.format('YYYY-MM-DDTHH:mm:ss'), tz)
      : parsed;
  }

  private static fromISO(isoString: string): Dayjs {
    return dayjs(isoString).tz(eUSTimeZone);
  }

  private static fromParts(
    {
      year = 1970,
      month = 1,
      day = 1,
      hour = 0,
      minute = 0,
      second = 0,
    } : AppDateObjectSupport
  ): Dayjs {
    const y = year.toString();
    const m = month.toString().padStart(2, '0');
    const d = day.toString().padStart(2, '0');
    const h = hour.toString().padStart(2, '0');
    const min = minute.toString().padStart(2, '0');
    const s = second.toString().padStart(2, '0');

    const str = `${y}-${m}-${d}T${h}:${min}:${s}`;

    return dayjs.tz(str, eUSTimeZone);
  }

  private static fromDate(date: Date): Dayjs {
    return dayjs(date).tz(eUSTimeZone);
  }

  private static now(): Dayjs {
    return dayjs.tz(new Date(), eUSTimeZone);
  }

  getDate(): Dayjs {
    return this.date;
  }

  getDateAsDateObject(): Date {
    return this.date.toDate();
  }

  getISOString(): string {
    return this.date.format();
  }

  getISOStringUTC(): string {
    return this.date.clone().utc().toISOString();
  }

  getISOStringWithFractionSeconds(): string {
    return this.date.toISOString();
  }

  getFormattedDate(format: DateFormat | TimeFormat): string {
    return this.date.format(format);
  }

  getUtcFormattedDate(format: DateFormat | TimeFormat, keepLocalTime?: boolean): string {
    return this.date.utc(keepLocalTime).format(format);
  }

  changeLocale(language: AvailableLanguages) {
    dayjs.locale(language);
  }

  getDay(): number {
    return this.date.day();
  }

  getDayOfMonth(): number {
    return this.date.date();
  }

  getMonth(): number {
    return this.date.month();
  }

  getQuarter(): number {
    return this.date.quarter();
  }

  getYear(): number {
    return this.date.year();
  }

  getTimestampInMiliseconds(): number {
    return this.date.valueOf();
  }

  isAfter(comparedDate: AppDate, unit?: OpUnitType): boolean {
    return this.date.isAfter(comparedDate.date, unit);
  }

  isBefore(comparedDate: AppDate, unit?: OpUnitType): boolean {
    return this.date.isBefore(comparedDate.date, unit);
  }

  isDateTheSame(otherDate: AppDate, unit?: OpUnitType): boolean {
    return this.date.isSame(otherDate.date, unit);
  }

  startOf(unit: OpUnitType): AppDate {
    return new AppDate(this.date.startOf(unit).toDate());
  }

  endOf(unit: OpUnitType): AppDate {
    return new AppDate(this.date.endOf(unit).toDate());
  }

  isValidDate(): boolean {
    return this.date.isValid();
  }

  set(unit: UnitType, value: number): AppDate {
    this.date = this.date.set(unit, value);

    return this;
  }

  setHour(hour: number): void {
    this.date = this.date.hour(hour);
  }

  setMonth(month: number): void {
    this.date = this.date.month(month);
  }

  setYear(year: number): void {
    this.date = this.date.year(year);
  }

  add(numOfUnits: number, units: ManipulateType): AppDate {
    this.date = this.date.add(numOfUnits, units);

    return this;
  }

  subtract(numOfUnits: number, units: ManipulateType): AppDate {
    this.date = this.date.subtract(numOfUnits, units);

    return this;
  }

  isBetween(startingDate: ConfigType, endDate: ConfigType, precision?: OpUnitType, includes?: DateExclusivity): boolean {
    return this.date.isBetween(startingDate, endDate, precision, includes);
  }

  isDateTheSameOrAfter(otherDate: AppDate, unit?: OpUnitType): boolean {
    return this.date.isSameOrAfter(otherDate.date, unit);
  }

  isDateTheSameOrBefore(otherDate: AppDate, unit?: OpUnitType): boolean {
    return this.date.isSameOrBefore(otherDate.date, unit);
  }

  getDatesDifference(otherDate: AppDate, unit: OpUnitType): number {
    return this.date.diff(otherDate.date, unit);
  }

  getUtcOffsetInMinutes(): number {
    return this.date.utcOffset();
  }

  setDateWithUtcOffset(offset: number | string): AppDate {
    this.date = this.date.utcOffset(offset);

    return this;
  }

  getUserTimeZone(): string {
    return dayjs.tz.guess();
  }
}